import _ from 'lodash';
import moment from 'moment';

import { sortClassTimes } from './sortClassTimes';
import { type ClassSessionGroup } from '../../../context/types';
import { AttendanceType, type CalendarValues, type ClassSession, type ClassSessionWithGroupData } from '../types';

export const calcSessions = (values: CalendarValues) => {
    const {
        days,
        timezone,
        dateRange,
        startTime,
        endTime,
        label,
        classSessionGroupUuid,
        selectedGroups,
        attendanceType
    } = values;

    // Adjusts the date range based on the selected timezone rather than the user's current timezone
    if (moment.isMoment(dateRange[0]) && moment.isMoment(dateRange[1])) {
        const sourceTimezoneOffset = dateRange[0].utcOffset();
        const targetTimezoneOffset = moment.tz(dateRange[0], timezone).utcOffset();
        const timezoneDifference = sourceTimezoneOffset - targetTimezoneOffset;

        dateRange[0].add(timezoneDifference, 'minutes');
        dateRange[0].tz(timezone);

        dateRange[1].add(timezoneDifference, 'minutes');
        dateRange[1].tz(timezone);
    }

    const startTimeDate = moment(startTime, 'HH:mm'); // only used for hours and minutes
    const startHours = startTimeDate.hours();
    const startMins = startTimeDate.minutes();

    const endTimeDate = moment(endTime, 'HH:mm'); // only used for hours and minutes
    const endHours = endTimeDate.hours();
    const endMins = endTimeDate.minutes();

    const a = moment.tz(dateRange[0] as unknown as string, 'L', timezone);
    const b = moment.tz(dateRange[1] as unknown as string, 'L', timezone);

    const sessions: ClassSession[] = [];

    for (let m = moment(a); m.diff(b, 'days') <= 0; m.add(1, 'days')) {
        if (days?.indexOf(m.day()) !== -1) {
            const sessionStart = m.clone().hours(startHours).minutes(startMins);
            const sessionEnd = m.clone().hours(endHours).minutes(endMins);

            const shouldAdd24Hours = moment(sessionStart).valueOf() > moment(sessionEnd).valueOf();
            const sessionEndPlus24 = moment(sessionEnd).startOf('minute').add(24, 'hours');

            const session: ClassSessionWithGroupData = {
                start_time: sessionStart.startOf('minute').unix(),
                end_time: shouldAdd24Hours
                    ? sessionEndPlus24.startOf('minute').unix()
                    : sessionEnd.startOf('minute').unix(),
                timezone,
                classSessionGroupUuid,
                label,
                selectedGroups,
                attendanceType
            };

            sessions.push(session);
        }
    }

    const firstSession = sessions[0];
    const isOvernight = firstSession
        ? moment.tz(firstSession.start_time * 1000, timezone).get('day') !==
          moment.tz(firstSession.end_time * 1000, timezone).get('day')
        : false;

    return {
        sessions,
        label,
        classSessionGroupUuid,
        isOvernight
    };
};

export const calcSessionsFromDates = (dates: CalendarValues[]): ClassSession[] => {
    const allSessions = dates.reduce((previous, date) => {
        const { sessions } = calcSessions(date);

        return [...previous, ...sessions];
    }, [] as ClassSession[]);

    const uniqueSessions = _.uniqBy(allSessions, (session) => {
        const startTimeToNearestMinute = moment.unix(session.start_time).startOf('minute').toISOString();
        const endTimeToNearestMinute = moment.unix(session.end_time).startOf('minute').toISOString();

        return startTimeToNearestMinute + endTimeToNearestMinute;
    });

    return uniqueSessions.sort((a, b) => moment.unix(a.start_time).valueOf() - moment.unix(b.start_time).valueOf());
};

interface FilterSessionsInterface {
    sessions: ClassSession[];
    excludedSessions: ClassSession[];
}

export const calcSessionsFromClassTimes = (
    classTimes: string,
    timezone: string,
    classSessionGroups: ClassSessionGroup[]
): ClassSession[] => {
    const classTimesArray = sortClassTimes(classTimes);

    return classTimesArray.map((startAndEndTime) => {
        const [startTime, endTime] = startAndEndTime.split(':');
        const computeAttendanceType = (trackAttendance: boolean, attendanceLmsPush: boolean) => {
            if (trackAttendance) {
                return AttendanceType.NoAttendance;
            }
            return attendanceLmsPush ? AttendanceType.IntegrateWithLms : AttendanceType.Native;
        };

        let classSessionGroupUuid;
        let label;
        let selectedGroups;
        let attendanceType = AttendanceType.Native;
        classSessionGroups.forEach((classSessionGroup) => {
            if (classSessionGroup && classSessionGroup.schedule && classSessionGroup.schedule.class_times) {
                if (classSessionGroup.schedule.class_times.indexOf(startAndEndTime) !== -1) {
                    classSessionGroupUuid = classSessionGroup.class_session_group_uuid;
                    label = classSessionGroup.config?.label;
                    selectedGroups = classSessionGroup.config?.selectedGroups;
                    attendanceType = computeAttendanceType(
                        !!classSessionGroup.config?.trackAttendance,
                        !!classSessionGroup.config?.attendanceLmsPush
                    );
                }
            }
        });

        return {
            start_time: parseInt(startTime, 10),
            end_time: parseInt(endTime, 10),
            timezone,
            classSessionGroupUuid,
            label,
            selectedGroups,
            attendanceType
        };
    });
};

export const filterSessions = ({ sessions, excludedSessions }: FilterSessionsInterface): ClassSession[] =>
    sessions.filter((session) => {
        const found = excludedSessions.find(
            (excludedSession) =>
                excludedSession.start_time === session.start_time && excludedSession.end_time === session.end_time
        );

        return !found;
    });
